import { Div, Label, Loader, useWindowSize, Select, Touchable, Icon } from "@grassrootly/rn-core";
import { ScrollView, Image, RefreshControl, View } from "react-native";
import { useCompetitionStats } from "./useCompetitionStats";
import { PlayerRating } from "../../components/shared";
import { images } from "../../layout/assets";
import { MainStackScreenProps } from "../../navigation/Navigation.types";
import { useMemo, useState } from "react";
import { ISeason } from "@grassrootly/api";

interface CompetitionPlayerStatsTabProps {
  competitionId: number;
  seasonId: number;
  season: ISeason | null;
  navigation: MainStackScreenProps<"competition-detail">["navigation"];
}

export default function CompetitionPlayerStatsTab({ competitionId, seasonId, season, navigation }: CompetitionPlayerStatsTabProps) {
  const { stats, loading, refreshStats, mode, setMode, month, navigateMonth, isCurrentOrFutureMonth } = useCompetitionStats(competitionId, seasonId);
  const { width } = useWindowSize();
  const [sortBy, setSortBy] = useState<"experience" | "rating" | "goals" | "assists" | "games" | "win_efficiency">("experience");

  const sortOptions = useMemo(
    () => [
      { value: "experience", label: "EXP" },
      { value: "rating", label: "Rating" },
      { value: "goals", label: "Goals" },
      { value: "assists", label: "Assists" },
      { value: "games", label: "Matches played" },
      { value: "win_efficiency", label: "Result efficiency" },
    ],
    []
  );

  // Sorted stats for the list
  const sortedStats = useMemo(() => {
    if (!stats) return [];
    return stats.slice().sort((a, b) => {
      switch (sortBy) {
        case "experience":
          return (b.points_earned ?? 0) - (a.points_earned ?? 0);
        case "rating":
          return (b.avg_rating ?? 0) - (a.avg_rating ?? 0);
        case "goals":
          return (b.goals ?? 0) - (a.goals ?? 0);
        case "assists":
          return (b.assists ?? 0) - (a.assists ?? 0);
        case "games":
          return (b.games_played ?? 0) - (a.games_played ?? 0);
        case "win_efficiency":
          return (b.player_win_efficiency ?? 0) - (a.player_win_efficiency ?? 0);
        default:
          return 0;
      }
    });
  }, [stats, sortBy]);

  return (
    <Div classNames={["flex1"]}>
      {loading ? (
        <Div classNames={["flex1", "alignItemsCenter", "justifyContentCenter", "mt-{30}"]}>
          <Loader />
        </Div>
      ) : null}

      <ScrollView refreshControl={<RefreshControl refreshing={loading} onRefresh={refreshStats} />} showsVerticalScrollIndicator={false}>
        {/* Mode toggle - pill chips with icons */}
        <Div classNames={["flexRow", "justifyContentCenter", "mt-{12}", "mb-{12}"]}>
          <Div classNames={["flexRow", "gap8", "bgSecondaryMain", "rounded", "ph8", "pv4", "alignItemsCenter"]}>
            <Touchable onPress={() => setMode("monthly")}>
              <Div
                classNames={[
                  "ph8",
                  "pv4",
                  "rounded",
                  mode === "monthly" ? "bgPrimaryWashed" : "",
                ]}
              >
                <Div classNames={["flexRow", "alignItemsCenter", "gap4"]}>
                  <Icon
                    name="calendar_today"
                    size={16}
                    classNames={[mode === "monthly" ? "colorPrimary" : "colorSecondaryContrast"]}
                  />
                  <Label
                    type={mode === "monthly" ? "l4Bold" : "l4Regular"}
                    classNames={[mode === "monthly" ? "colorPrimary" : "colorSecondaryContrast"]}
                  >
                    Monthly
                  </Label>
                </Div>
              </Div>
            </Touchable>

            <Touchable onPress={() => setMode("allseason")}>
              <Div
                classNames={[
                  "ph8",
                  "pv4",
                  "rounded",
                  mode === "allseason" ? "bgPrimaryWashed" : "",
                ]}
              >
                <Div classNames={["flexRow", "alignItemsCenter", "gap4"]}>
                  <Icon
                    name="calendar_month"
                    size={16}
                    classNames={[mode === "allseason" ? "colorPrimary" : "colorSecondaryContrast"]}
                  />
                  <Label
                    type={mode === "allseason" ? "l4Bold" : "l4Regular"}
                    classNames={[mode === "allseason" ? "colorPrimary" : "colorSecondaryContrast"]}
                  >
                    All Season
                  </Label>
                </Div>
              </Div>
            </Touchable>
          </Div>
        </Div>

        {/* Month navigator for monthly mode (placed below mode filters) - pill-style stepper */}
        {mode === "monthly" && (
          <Div classNames={["flexRow", "justifyContentCenter", "mt-{4}", "mb-{20}"]}>
            <Div classNames={["flexRow", "alignItemsCenter", "gap12", "bgSecondaryMain", "rounded", "ph12", "pv4"]}>
              {/* Previous month */}
              <Touchable onPress={() => navigateMonth(-1)}>
                <Icon name="arrow_back_ios" size={20} />
              </Touchable>

              {/* Current month */}
              <Div classNames={["flexRow", "alignItemsCenter", "ph-{12}"]}>
                <Label classNames={["textCenter"]} type="l2Bold">
                  {month}
                </Label>
              </Div>

              {/* Next month (hidden for current/future) */}
              {!isCurrentOrFutureMonth && (
                <Touchable onPress={() => navigateMonth(1)}>
                  <Icon name="arrow_forward_ios" size={20} />
                </Touchable>
              )}
            </Div>
          </Div>
        )}

        {/* Stats Section Header */}
        {!loading && stats && stats.length > 0 && (
          <View>
            <Div classNames={["mt-{8}", "mb-{8}", "alignItemsCenter"]}>
              <Label type="l1Bold" classNames={["textCenter"]}>{mode === "monthly" ? `Top stats - ${month}` : "Season top stats"}</Label>
            </Div>
          </View>
        )}

        {/* Empty state when there are no stats */}
        {!loading && (!stats || stats.length === 0) && (
          <Div classNames={["mt-{8}", "mb-{20}", "alignItemsCenter"]}>
            <Label type="l2Regular" classNames={["textCenter", "colorSecondaryLight"]}>
              There was no matches played.
            </Label>
          </Div>
        )}

        {/* Sorting Control */}
        {!loading && stats && stats.length > 0 && (
          <Div classNames={["flexRow", "w100", "justifyContentFlexEnd", "mt-{12}", "mb-{8}", "alignItemsCenter", "gap8", "ml-{auto}"]}>
            <Select
              options={sortOptions as any}
              selectedOption={sortBy}
              onSelect={(item: any) => setSortBy(item.value)}
              placeholderText="Select filter"
              forceSelectedOption
            />
          </Div>
        )}

        {/* Player Stats List - Show only relevant stat for selected filter */}
        {!loading && sortedStats && sortedStats.length > 0 && (
          <Div classNames={["mt-{8}"]}>
            {sortedStats.map((player, index) => (
              <Touchable
                key={`${player.player_id}-${index}`}
                classNames={[
                  `${width > 720 ? "p16" : "p8"}`,
                  index % 2 !== 0 ? "bgSecondaryMain" : "",
                  "flexRow",
                  "alignItemsCenter",
                ]}
                onPress={() => navigation.navigate("eqipa-card", { playerId: player.player_id })}
              >
                <Div classNames={["flex1"]}>
                  <Div classNames={["flexRow", "alignItemsCenter", "justifyContentSpaceBetween"]}>
                    <Div classNames={["alignItemsCenter"]}>
                      <View style={{ position: 'relative' }}>
                        <Div classNames={["bgPrimaryWashed", "p8", "rounded"]}>
                          <Image
                            style={{ height: width > 720 ? 40 : 30, width: width > 720 ? 40 : 30, borderRadius: 40 }}
                            resizeMode="cover"
                            source={player.player_profile_picture ? { uri: player.player_profile_picture } : images.playerAvatar}
                          />
                        </Div>
                        {player.player_position === 'GK' && (
                          <View style={{ 
                            position: 'absolute', 
                            bottom: 0, 
                            right: 0, 
                            backgroundColor: '#3B82F6', 
                            borderRadius: 6, 
                            paddingHorizontal: 3, 
                            paddingVertical: 1 
                          }}>
                            <Label type="l4Bold" classNames={["colorNeutralLighterLight"]}>GK</Label>
                          </View>
                        )}
                      </View>
                    </Div>
                    <Div classNames={["flex1", "ml-{12}"]}>
                      <Label numberOfLines={2} ellipsizeMode="tail" type={width > 720 ? "l2Regular" : "l3Regular"}>
                        {player.player_name}
                      </Label>
                      {player.group_logo && (
                        <Div classNames={["flexRow", "alignItemsCenter", "mt-{2}", "gap4"]}>
                          <Image
                            source={{ uri: player.group_logo }}
                            style={{ width: 14, height: 14, borderRadius: 14 }}
                          />
                          <Label type="l4Regular" classNames={["colorSecondaryLight"]} numberOfLines={1}>
                            {player.group_name}
                          </Label>
                        </Div>
                      )}
                    </Div>

                    <Div classNames={["flexRow", "alignItemsCenter", "gap8"]}>
                      {sortBy === "rating" && typeof player.avg_rating === "number" && player.avg_rating > 0 && (
                        <Div classNames={["bgPrimaryWashed", "p8", "rounded"]}>
                          <PlayerRating rating={Number(player.avg_rating.toFixed(2))} decimalPlaces={2} classNames={["p-{4}"]} />
                        </Div>
                      )}
                      {sortBy === "experience" && (
                        <Div classNames={["bgPrimaryWashed", "p8", "rounded", "flexRow", "alignItemsCenter", "gap4"]}>
                          <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                            {player.points_earned.toFixed(1)}
                          </Label>
                          <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                            exp.
                          </Label>
                        </Div>
                      )}
                      {sortBy === "goals" && (
                        <Div classNames={["bgPrimaryWashed", "p8", "rounded", "flexRow", "alignItemsCenter", "gap4"]}>
                          <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                            {player.goals ?? 0}
                          </Label>
                          <Icon name="sports_soccer" size={18} />
                        </Div>
                      )}
                      {sortBy === "assists" && (
                        <Div classNames={["bgPrimaryWashed", "p8", "rounded", "flexRow", "alignItemsCenter", "gap4"]}>
                          <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                            {player.assists ?? 0}
                          </Label>
                          <Image source={images.assist} style={{ height: 14, width: 24 }} />
                        </Div>
                      )}
                      {sortBy === "games" && (
                        <Div classNames={["bgPrimaryWashed", "p8", "rounded", "flexRow", "alignItemsCenter", "gap4"]}>
                          <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                            {player.games_played ?? 0}
                          </Label>
                        </Div>
                      )}
                      {sortBy === "win_efficiency" && (
                        <Div classNames={["bgPrimaryWashed", "p8", "rounded", "flexRow", "alignItemsCenter", "gap4"]}>
                          <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                            {Number((player.player_win_efficiency * 100).toFixed(1))}
                          </Label>
                          <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                            %
                          </Label>
                        </Div>
                      )}
                    </Div>
                  </Div>
                </Div>
              </Touchable>
            ))}
          </Div>
        )}
      </ScrollView>
    </Div>
  );
}
